import React, { useState, useEffect } from "react";
import { Card, Row, Col, Select, InputNumber, Button, Tag, Empty, Spin, Pagination, message } from "antd";
import { AimOutlined, EnvironmentOutlined, HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getPublicRooms, getRegions, getStreets } from "../services/api";
import PagePicture from "../components/PagePicture";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";
import { getBrowserLocation, locationErrorText, validCoordinates } from "../utils/geolocation";

let publicPoolCache = null;
const nearbyLocationKey = "kanwuzi-nearby-location";
const readNearbyLocation = () => {
    try {
        const value = JSON.parse(sessionStorage.getItem(nearbyLocationKey) || "null");
        return validCoordinates(value) ? value : null;
    } catch (error) {
        return null;
    }
};
const formatDistance = value => Number(value) < 1 ? `${Math.max(1, Math.round(Number(value) * 1000))}米` : `${Number(value).toFixed(Number(value) < 10 ? 1 : 0)}公里`;

const HomeScreen = () => {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState(publicPoolCache?.rooms || []);
    const [loading, setLoading] = useState(!publicPoolCache);
    const [regions, setRegions] = useState(publicPoolCache?.regions || []);
    const [streets, setStreets] = useState([]);
    const [pagination, setPagination] = useState(publicPoolCache?.pagination || { page: 1, limit: 20, total: 0 });
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [filters, setFilters] = useState({ street_id: null, min_rent: null, max_rent: null, bedrooms: null });
    const [nearbyLocation, setNearbyLocation] = useState(readNearbyLocation);
    const [locating, setLocating] = useState(false);

    useEffect(() => { loadRegions(); loadRooms(1, null, Boolean(publicPoolCache), readNearbyLocation()); }, []);

    const loadRegions = async () => { try { const res = await getRegions(); setRegions(res); publicPoolCache = { ...(publicPoolCache || {}), regions: res }; } catch (e) {} };
    
    const loadStreets = async (regionId) => {
        try { 
            const res = await getStreets({ region_id: regionId }); 
            setStreets(res); 
            // 选择区域后清空街道选择并搜索
            setFilters(f => ({ ...f, street_id: null }));
        } catch (e) {} 
    };

    const loadRooms = async (page = 1, newFilters = null, quiet = false, locationOverride = nearbyLocation) => {
        try {
            if (!quiet) setLoading(true);
            const currentFilters = newFilters || filters;
            const locationParams = validCoordinates(locationOverride) ? { latitude: locationOverride.latitude, longitude: locationOverride.longitude } : {};
            const res = await getPublicRooms({ ...currentFilters, ...locationParams, page, limit: 20 });
            setRooms(res.data);
            setPagination(res.pagination);
            const isDefaultView = page === 1 && Object.values(currentFilters).every(value => value === null || value === undefined || value === "");
            if (isDefaultView) publicPoolCache = { ...(publicPoolCache || {}), rooms: res.data, pagination: res.pagination };
        } catch (e) { 
            message.error("加载失败"); 
        } finally { 
            if (!quiet) setLoading(false);
        }
    };

    const handleRegionChange = (regionId) => {
        setSelectedRegion(regionId);
        if (regionId) {
            loadStreets(regionId);
        } else {
            setStreets([]);
            setFilters(f => ({ ...f, street_id: null }));
        }
    };

    const handleStreetChange = (streetId) => {
        const newFilters = { ...filters, street_id: streetId };
        setFilters(newFilters);
        loadRooms(1, newFilters);
    };

    const handleRentChange = (type, value) => {
        const newFilters = { ...filters, [type]: value };
        setFilters(newFilters);
    };

    const handleBedroomsChange = (value) => {
        const newFilters = { ...filters, bedrooms: value };
        setFilters(newFilters);
    };

    const handleSearch = () => {
        loadRooms(1, filters);
    };

    const handleLocate = async () => {
        if (locating) return;
        setLocating(true);
        try {
            const next = await getBrowserLocation();
            setNearbyLocation(next);
            try { sessionStorage.setItem(nearbyLocationKey, JSON.stringify(next)); } catch (error) { /* session cache is optional */ }
            message.success("定位成功，已优先展示离你近的房源");
            await loadRooms(1, filters, false, next);
        } catch (error) {
            message.warning(locationErrorText(error));
        } finally {
            setLocating(false);
        }
    };

    const clearNearby = async () => {
        setNearbyLocation(null);
        try { sessionStorage.removeItem(nearbyLocationKey); } catch (error) { /* session cache is optional */ }
        await loadRooms(1, filters, false, null);
    };

    const decorMap = { rough: "毛坯", simple: "简装", fine: "精装", luxury: "豪装", apartment: "公寓", community: "小区" };
    const depositMap = { one_for_one: "押一付一", two_for_one: "押二付一" };
    const feeText = value => value === null || value === undefined || value === "" ? "未填写" : `¥${Number(value).toFixed(2)}`;
    const depositText = room => room.deposit_method === "other" ? (room.deposit_method_other || "其他") : (depositMap[room.deposit_method] || "未填写");

    return (
        <div>
            {loading && <PageLoadingOverlay caption="正在加载房源，请稍候…" />}
            <PagePicture
                desktopSlot="public_pool_banner_desktop"
                mobileSlot="public_pool_banner_mobile"
                className="public-pool-banner"
                alt="看屋子公共池宣传图"
            />
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24}>
                        <div className="nearby-location-bar">
                            <Button icon={<AimOutlined />} loading={locating} disabled={locating} onClick={handleLocate}>{nearbyLocation ? "重新定位" : "获取定位，附近优先"}</Button>
                            {nearbyLocation && <><Tag color="green">已按距离优先</Tag><Button type="link" onClick={clearNearby}>取消就近排序</Button></>}
                            <span>当前位置仅用于本次附近排序，不会保存到服务器</span>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select 
                            placeholder="选择区域" 
                            style={{ width: "100%" }} 
                            allowClear 
                            value={selectedRegion}
                            onChange={handleRegionChange}
                        >
                            {regions.map(r => <Select.Option key={r.id} value={r.id}>{r.name}</Select.Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select 
                            placeholder="选择街道" 
                            style={{ width: "100%" }} 
                            allowClear 
                            value={filters.street_id} 
                            onChange={handleStreetChange}
                            disabled={!selectedRegion}
                        >
                            {streets.map(s => <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>)}
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                        <InputNumber 
                            placeholder="最低租金" 
                            style={{ width: "100%" }} 
                            value={filters.min_rent} 
                            onChange={(v) => handleRentChange("min_rent", v)} 
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                        <InputNumber 
                            placeholder="最高租金" 
                            style={{ width: "100%" }} 
                            value={filters.max_rent} 
                            onChange={(v) => handleRentChange("max_rent", v)} 
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                        <Select 
                            placeholder="户型" 
                            style={{ width: "100%" }} 
                            allowClear 
                            value={filters.bedrooms} 
                            onChange={handleBedroomsChange}
                        >
                            <Select.Option value={1}>1室</Select.Option>
                            <Select.Option value={2}>2室</Select.Option>
                            <Select.Option value={3}>3室</Select.Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} block>搜索</Button>
                    </Col>
                </Row>
            </Card>
            <Spin spinning={loading}>
                {rooms.length === 0 && !loading ? <Empty description="暂无房源" /> : (
                    <>
                        <Row gutter={[16, 16]}>
                            {rooms.map(room => (
                                <Col key={room.id} xs={12} sm={12} lg={6}>
                                    <Card className="room-card" hoverable onClick={() => navigate("/room/" + room.id)} cover={
                            <div style={{ aspectRatio: "1 / 1", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                                {room.images && room.images.length > 0 ? (
                                    <img src={room.images[0]} alt="房源图片" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                    <HomeOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
                                )}
                            </div>
                        }>
                                        <div style={{ marginBottom: 8, fontWeight: "bold" }}>{room.bedrooms}室{(room.living_rooms || 0)}厅 - {room.room_number}</div>
                                        <div style={{ fontSize: "clamp(16px, 4vw, 20px)", color: "#ff4d4f", fontWeight: "bold", marginBottom: 8 }}>¥{room.rent}/月</div>
                                        <div className="room-card-address" style={{ color: "#8c8c8c", marginBottom: 8 }}><EnvironmentOutlined /> {room.region_name || ""} {room.street_name || ""} {room.building_name || ""} {room.room_number}</div>
                                        <div>
                                            {Number.isFinite(Number(room.distance_km)) && room.distance_km !== null && <Tag color="green"><AimOutlined /> 距你约 {formatDistance(room.distance_km)}</Tag>}
                                            {room.decoration && <Tag color="blue">{decorMap[room.decoration] || room.decoration}</Tag>}
                                            {room.commission !== undefined && <Tag color="gold">佣金 ¥{Number(room.commission).toFixed(2)}</Tag>}
                                        </div>
                                        <div className="room-card-fees">
                                            <span>水费：{feeText(room.water_price)}/方</span>
                                            <span>电费：{feeText(room.electricity_price)}/度</span>
                                            <span>管理费：{feeText(room.management_fee)}/月</span>
                                            <span>押金：{depositText(room)}</span>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                        <div style={{ textAlign: "center", marginTop: 24 }}><Pagination current={pagination.page} total={pagination.total} pageSize={pagination.limit} onChange={(p) => loadRooms(p)} showTotal={(total) => "共 " + total + " 套房源"} /></div>
                    </>
                )}
            </Spin>
        </div>
    );
};

export default HomeScreen;
